import { useCallback, useEffect, useState } from "react";
import type { WidgetConfig, WidgetZone } from "../types";

export const DEFAULT_PERSONAL_WIDGETS: WidgetConfig[] = [
  { id: "quick-stats", visible: true, order: 1, size: "full", moduleKeys: ["tasks", "actions", "meetings", "milestones"] },
  { id: "my-tasks", visible: true, order: 2, size: "half", moduleKeys: ["tasks"] },
  { id: "upcoming-meetings", visible: true, order: 3, size: "half", moduleKeys: ["meetings"] },
  { id: "my-actions", visible: true, order: 4, size: "half", moduleKeys: ["actions"] },
  { id: "overdue-items", visible: true, order: 5, size: "half", moduleKeys: ["tasks", "actions"] },
  { id: "recent-notes", visible: false, order: 6, size: "half", moduleKeys: ["notes"] },
  { id: "my-ideas", visible: false, order: 7, size: "half", moduleKeys: ["ideas"] },
];

export const DEFAULT_PLATFORM_WIDGETS: WidgetConfig[] = [
  { id: "platform-counts", visible: true, order: 1, size: "full", moduleKeys: ["companies", "products", "projects", "meetings", "tasks", "actions", "notes", "ideas", "decisions", "milestones", "budgets", "expenses", "media"] },
  { id: "project-health", visible: true, order: 2, size: "half", moduleKeys: ["projects"] },
  { id: "recent-activity", visible: true, order: 3, size: "half", moduleKeys: ["audit_center"] },
  { id: "upcoming-milestones", visible: true, order: 4, size: "half", moduleKeys: ["milestones"] },
  { id: "pending-expenses", visible: true, order: 5, size: "half", moduleKeys: ["expenses"] },
  { id: "budget-summary", visible: false, order: 6, size: "half", moduleKeys: ["budgets", "expenses"] },
  { id: "expiring-credentials", visible: false, order: 7, size: "half", moduleKeys: ["access_vault"] },
  { id: "ideas-leaderboard", visible: false, order: 8, size: "half", moduleKeys: ["ideas"] },
];

function personalKey(userId: string): string {
  return `dashboard_personal_${userId}`;
}
function platformKey(userId: string): string {
  return `dashboard_platform_${userId}`;
}

function loadFromStorage(key: string): WidgetConfig[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    // Normalize shape to avoid stale-field issues
    return parsed
      .filter(
        (w): w is WidgetConfig =>
          w &&
          typeof w === "object" &&
          typeof w.id === "string" &&
          typeof w.visible === "boolean" &&
          typeof w.order === "number" &&
          (w.size === "full" || w.size === "half"),
      )
      .map((w) => ({ ...w }));
  } catch {
    return null;
  }
}

function mergeWithDefaults(
  stored: WidgetConfig[] | null,
  defaults: WidgetConfig[],
): WidgetConfig[] {
  if (!stored) return defaults.map((w) => ({ ...w }));
  const storedMap = new Map(stored.map((w) => [w.id, w]));
  // Include any new defaults that the user hasn't seen yet, and drop
  // any stored widgets that no longer exist in defaults.
  const defaultIds = new Set(defaults.map((w) => w.id));
  const merged: WidgetConfig[] = [];
  for (const d of defaults) {
    const s = storedMap.get(d.id);
    if (s) merged.push({ ...d, visible: s.visible, order: s.order });
    else merged.push({ ...d });
  }
  // Preserve order from stored state when present
  merged.sort((a, b) => a.order - b.order);
  // Drop any leftover entries that aren't in defaults
  return merged.filter((w) => defaultIds.has(w.id));
}

export function useWidgetLayout(userId: string) {
  const [personalWidgets, setPersonalWidgets] = useState<WidgetConfig[]>(() =>
    mergeWithDefaults(loadFromStorage(personalKey(userId)), DEFAULT_PERSONAL_WIDGETS),
  );
  const [platformWidgets, setPlatformWidgets] = useState<WidgetConfig[]>(() =>
    mergeWithDefaults(loadFromStorage(platformKey(userId)), DEFAULT_PLATFORM_WIDGETS),
  );
  const [isEditMode, setIsEditMode] = useState(false);

  // Re-hydrate when the user changes (e.g. after login switch).
  useEffect(() => {
    setPersonalWidgets(
      mergeWithDefaults(loadFromStorage(personalKey(userId)), DEFAULT_PERSONAL_WIDGETS),
    );
    setPlatformWidgets(
      mergeWithDefaults(loadFromStorage(platformKey(userId)), DEFAULT_PLATFORM_WIDGETS),
    );
    setIsEditMode(false);
  }, [userId]);

  const saveLayout = useCallback(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      personalKey(userId),
      JSON.stringify(personalWidgets),
    );
    window.localStorage.setItem(
      platformKey(userId),
      JSON.stringify(platformWidgets),
    );
    setIsEditMode(false);
  }, [userId, personalWidgets, platformWidgets]);

  const resetLayout = useCallback(() => {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(personalKey(userId));
      window.localStorage.removeItem(platformKey(userId));
    }
    setPersonalWidgets(DEFAULT_PERSONAL_WIDGETS.map((w) => ({ ...w })));
    setPlatformWidgets(DEFAULT_PLATFORM_WIDGETS.map((w) => ({ ...w })));
    setIsEditMode(false);
  }, [userId]);

  const toggleWidget = useCallback(
    (zone: WidgetZone, id: string) => {
      const setter =
        zone === "personal" ? setPersonalWidgets : setPlatformWidgets;
      setter((prev) =>
        prev.map((w) => (w.id === id ? { ...w, visible: !w.visible } : w)),
      );
    },
    [],
  );

  const moveWidget = useCallback(
    (zone: WidgetZone, id: string, direction: "up" | "down") => {
      const setter =
        zone === "personal" ? setPersonalWidgets : setPlatformWidgets;
      setter((prev) => {
        const sorted = [...prev].sort((a, b) => a.order - b.order);
        const idx = sorted.findIndex((w) => w.id === id);
        if (idx === -1) return prev;
        const swapIdx = direction === "up" ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= sorted.length) return prev;
        const a = sorted[idx];
        const b = sorted[swapIdx];
        const tmp = a.order;
        a.order = b.order;
        b.order = tmp;
        return sorted.map((w) => ({ ...w }));
      });
    },
    [],
  );

  return {
    personalWidgets,
    platformWidgets,
    isEditMode,
    setIsEditMode,
    saveLayout,
    resetLayout,
    toggleWidget,
    moveWidget,
  };
}
